import { useAction, useAtom } from '@reatom/npm-react'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'

import { UserTradingAnalyticsMcDistributionThreshold } from '../../models/user-trading-analytics-mc-distribution'

import { AvgBuyMcDistribution } from './avg-buy-mc-distribution'
import { AvgBuyMcDistributionError } from './avg-buy-mc-distribution-error'
import { AvgBuyMcDistributionSkeleton } from './avg-buy-mc-distribution-skeleton'

import type { UserTradingAnalyticsModel } from '../../atoms'

type PhishingCheckBlockProps = {
  model: UserTradingAnalyticsModel
}

export const AvgBuyMcDistributionBlock = ({
  model,
}: PhishingCheckBlockProps) => {
  const { t } = useTranslation()

  const [analyticsState] = useAtom(model.requestStateAtom)

  const onRetry = useAction(model.requestStateAtom.refresh)

  return AsyncStateN.matchWaiting(analyticsState, {
    Success: (data) => {
      const { mcDistribution } = data
      const below100k =
        mcDistribution[UserTradingAnalyticsMcDistributionThreshold.Below100k]
      const from100kTo500k =
        mcDistribution[
          UserTradingAnalyticsMcDistributionThreshold.From100kTo500k
        ]
      const above500k =
        mcDistribution[UserTradingAnalyticsMcDistributionThreshold.Above500k]

      return (
        <AvgBuyMcDistribution
          columns={[
            {
              title: t('profile.avg_buy_mc_distribution.below_100k'),
              count: below100k.count,
              percentageValue: below100k.percent,
              percentage: (
                <>
                  (<Numeric preset="percents" value={below100k.percent} />
                  %)
                </>
              ),
            },
            {
              title: t('profile.avg_buy_mc_distribution.from_100k_to_500k'),
              count: from100kTo500k.count,
              percentageValue: from100kTo500k.percent,
              percentage: (
                <>
                  (<Numeric preset="percents" value={from100kTo500k.percent} />
                  %)
                </>
              ),
            },
            {
              title: t('profile.avg_buy_mc_distribution.above_500k'),
              count: above500k.count,
              percentageValue: above500k.percent,
              percentage: (
                <>
                  (<Numeric preset="percents" value={above500k.percent} />
                  %)
                </>
              ),
            },
          ]}
        />
      )
    },
    Error: () => <AvgBuyMcDistributionError onRetry={onRetry} />,
    Waiting: () => <AvgBuyMcDistributionSkeleton />,
  })
}
